import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Upload, ExternalLink, UserPlus, MapPin, Image as ImageIcon, Film } from "lucide-react";
import api, { errMsg } from "../lib/api";
import SearchSelectAddNew from "./SearchSelectAddNew";
import { useAuth } from "../context/AuthContext";

export default function CustomerContactsSites({ customer, onClose }) {
  const { user } = useAuth();
  const canEdit = user.role === "admin" || (user.permissions || []).includes("edit_customer");
  const cid = customer.id;
  const [contacts, setContacts] = useState([]);
  const [sites, setSites] = useState([]);
  const [cf, setCf] = useState({ name: "", designation: "", mobile: "", email: "" });
  const [sf, setSf] = useState({ siteStatus: "", note: "" });
  const [customSite, setCustomSite] = useState(false);
  const SITE_PRESETS = ["Ground Floor", "1st Floor"];
  const [siteFile, setSiteFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([api.get(`/customers/${cid}/contacts`), api.get(`/customers/${cid}/sites`)]);
      setContacts(c.data || []); setSites(s.data || []);
    } catch { /* noop */ }
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const addContact = async () => {
    if (!cf.name.trim()) return toast.error("Contact name is required.");
    try { await api.post(`/customers/${cid}/contacts`, cf); toast.success("Contact added"); setCf({ name: "", designation: "", mobile: "", email: "" }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delContact = async (id) => { try { await api.delete(`/customers/${cid}/contacts/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  const addSite = async () => {
    if (!sf.siteStatus.trim()) return toast.error("Site status is required.");
    setBusy(true);
    try {
      const fd = new FormData();
      if (siteFile) fd.append("file", siteFile);
      await api.post(`/customers/${cid}/sites`, fd, { params: { siteStatus: sf.siteStatus, note: sf.note }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Site added"); setSf({ siteStatus: "", note: "" }); setCustomSite(false); setSiteFile(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const delSite = async (id) => { try { await api.delete(`/customers/${cid}/sites/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const addMedia = async (siteId, f) => {
    if (!f) return;
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/customers/${cid}/sites/${siteId}/media`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Media added"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delMedia = async (siteId, fileId) => { try { await api.delete(`/customers/${cid}/sites/${siteId}/file/${fileId}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const openFile = (siteId, fileId) => {
    api.get(`/customers/${cid}/sites/${siteId}/file/${fileId}`, { params: { mode: "view" }, responseType: "blob" })
      .then(({ data }) => { const u = URL.createObjectURL(data); window.open(u, "_blank"); setTimeout(() => URL.revokeObjectURL(u), 60000); })
      .catch((e) => toast.error(errMsg(e)));
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="customer-manage-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-head text-xl font-bold">{customer.customerName} — Contacts &amp; Sites</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact persons */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><UserPlus className="h-4 w-4 text-amber-600" /> Contact Persons</h4>
            <div className="space-y-2 mb-3">
              {contacts.length === 0 && <div className="text-sm text-slate-400">No contact persons yet.</div>}
              {contacts.map((c) => (
                <div key={c.contactId} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2" data-testid={`contact-${c.contactId}`}>
                  <div className="text-sm"><div className="font-semibold">{c.name} {c.designation && <span className="text-slate-400 font-normal">· {c.designation}</span>}</div><div className="text-xs text-slate-500">{c.mobile} {c.email && `· ${c.email}`}</div></div>
                  {canEdit && <button className="text-red-600 p-1" onClick={() => delContact(c.contactId)} data-testid={`contact-del-${c.contactId}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <input className="input" placeholder="Name *" value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })} data-testid="contact-name" />
                <SearchSelectAddNew master="designation" value={cf.designation} onChange={(v) => setCf({ ...cf, designation: v })} onSelect={(r) => setCf({ ...cf, designation: r.name })} placeholder="Designation" testid="contact-designation" />
                <div className="grid grid-cols-2 gap-2">
                  <input className="input" placeholder="Mobile" value={cf.mobile} onChange={(e) => setCf({ ...cf, mobile: e.target.value })} data-testid="contact-mobile" />
                  <input className="input" placeholder="Email" value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })} data-testid="contact-email" />
                </div>
                <button className="btn-accent btn-sm w-full" onClick={addContact} data-testid="contact-add"><Plus className="h-4 w-4" /> Add Contact</button>
              </div>
            )}
          </div>

          {/* Sites */}
          <div>
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2"><MapPin className="h-4 w-4 text-amber-600" /> Sites</h4>
            <div className="space-y-2 mb-3">
              {sites.length === 0 && <div className="text-sm text-slate-400">No sites yet.</div>}
              {sites.map((s) => (
                <div key={s.siteId} className="border border-slate-200 rounded-lg px-3 py-2" data-testid={`site-${s.siteId}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm"><span className="badge bg-slate-100 text-slate-700 border border-slate-200">{s.siteStatus || "—"}</span> {s.note && <span className="text-slate-600 ml-1">{s.note}</span>}</div>
                    {canEdit && <button className="text-red-600 p-1" onClick={() => delSite(s.siteId)} data-testid={`site-del-${s.siteId}`}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(s.files || []).map((f) => (
                      <div key={f.fileId} className="relative group">
                        <button className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 hover:bg-slate-50" title={f.filename} onClick={() => openFile(s.siteId, f.fileId)} data-testid={`site-media-${f.fileId}`}>
                          {["MP4", "MOV", "AVI", "WEBM"].includes((f.fileType || "").toUpperCase()) ? <Film className="h-3.5 w-3.5 text-slate-500" /> : <ImageIcon className="h-3.5 w-3.5 text-slate-500" />}
                          <span className="max-w-[90px] truncate">{f.filename}</span>
                        </button>
                        {canEdit && <button className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-slate-200 p-0.5 opacity-0 group-hover:opacity-100" onClick={() => delMedia(s.siteId, f.fileId)} data-testid={`site-media-del-${f.fileId}`}><X className="h-3 w-3 text-red-600" /></button>}
                      </div>
                    ))}
                    {canEdit && (
                      <label className="text-xs border border-dashed border-slate-300 rounded px-2 py-1 cursor-pointer hover:bg-slate-50 flex items-center gap-1 text-slate-500" data-testid={`site-media-add-${s.siteId}`}>
                        <Plus className="h-3.5 w-3.5" /> Add media
                        <input type="file" className="hidden" accept="image/*,video/*" onChange={(e) => addMedia(s.siteId, e.target.files[0])} />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {canEdit && (
              <div className="space-y-2 border-t border-slate-100 pt-3">
                <select className="input" data-testid="site-status"
                  value={customSite ? "Custom" : sf.siteStatus}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "Custom") { setCustomSite(true); setSf({ ...sf, siteStatus: "" }); }
                    else { setCustomSite(false); setSf({ ...sf, siteStatus: v }); }
                  }}>
                  <option value="">Site Status *</option>
                  {SITE_PRESETS.map((o) => <option key={o} value={o}>{o}</option>)}
                  <option value="Custom">Custom…</option>
                </select>
                {customSite && <input className="input" placeholder="Enter custom site status" value={sf.siteStatus} onChange={(e) => setSf({ ...sf, siteStatus: e.target.value })} data-testid="site-status-custom" />}
                <textarea className="input h-16 py-2" placeholder="Special note about site" value={sf.note} onChange={(e) => setSf({ ...sf, note: e.target.value })} data-testid="site-note" />
                <label className="btn-outline btn-sm w-full cursor-pointer justify-center"><Upload className="h-4 w-4" /> {siteFile ? siteFile.name : "Attach photo / video"}<input type="file" className="hidden" accept="image/*,video/*" onChange={(e) => setSiteFile(e.target.files[0])} data-testid="site-file" /></label>
                <button className="btn-accent btn-sm w-full" onClick={addSite} disabled={busy} data-testid="site-add"><Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add Site"}</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
